import {
  CompanyCoreValue,
  CompanyInfo,
  CompanyLocation,
  CompanyPhase,
  CompanyPhaseStatus,
  DiamondPillar,
  PayClass,
} from '@core/models';

import { CompanyDto, CoreValueIconDto, LocationDto, PayClassDto, PhaseStatusDto } from './company.dto';

/**
 * The ONE translation between the Company API's wire shapes and the app's models.
 *
 * It exists so the server's vocabulary stops here. The app has said `founded`,
 * `hq`, `videoUrl` and lowercase phase statuses everywhere for a long time; the
 * server says `foundedDt`, `headquarters`, `welcomeVideoUrl` and `CURRENT`.
 * Renaming the app to match would touch every Company template for no gain —
 * and the next module's contract would move the goalposts again. Translating in
 * one file keeps both sides free to use their own words.
 *
 * The app's `CompanyInfo` also NESTS the three lists inside itself, where the DTO
 * has them beside `profile`. That nesting is what the page reads, so this rebuilds
 * it rather than reshaping the page.
 */

// ── Phase status ────────────────────────────────────────────────────────────

const PHASE_STATUS_FROM: Record<PhaseStatusDto, CompanyPhaseStatus> = {
  CURRENT: 'active',
  PLANNED: 'planned',
  COMPLETE: 'complete',
};

const PHASE_STATUS_TO: Record<CompanyPhaseStatus, PhaseStatusDto> = {
  active: 'CURRENT',
  planned: 'PLANNED',
  complete: 'COMPLETE',
};

export function phaseStatusFromDto(s: PhaseStatusDto | null | undefined): CompanyPhaseStatus {
  return (s && PHASE_STATUS_FROM[s]) ?? 'planned';
}

export function phaseStatusToDto(s: CompanyPhaseStatus): PhaseStatusDto {
  return PHASE_STATUS_TO[s] ?? 'PLANNED';
}

// ── Core-value icons ────────────────────────────────────────────────────────
// The server's token set and the app's icon keys are the same thirteen icons
// spelled differently (STAR ↔ Star). Kept as an explicit table rather than a
// case transform so an added token fails loudly here instead of rendering blank.

const ICON_FROM: Record<CoreValueIconDto, string> = {
  STAR: 'Star',
  HEART: 'Heart',
  SHIELD: 'Shield',
  LIGHTBULB: 'Lightbulb',
  COMMENT: 'Comment',
  GLOBE: 'Globe',
  USERS: 'Users',
  FLAG: 'Flag',
  BRIEFCASE: 'Briefcase',
  BOLT: 'Bolt',
  CHECK: 'Check',
  BUILDING: 'Building',
  COMPASS: 'Compass',
};

const ICON_TO = Object.fromEntries(Object.entries(ICON_FROM).map(([token, key]) => [key, token])) as Record<
  string,
  CoreValueIconDto
>;

/** Token → the app's icon key. `null` (nobody chose one) falls back to Star. */
export function iconFromDto(token: CoreValueIconDto | null): string {
  return token ? ICON_FROM[token] ?? 'Star' : 'Star';
}

/** The app's icon key → token, or null when it maps to nothing the server knows. */
export function iconToDto(key: string): CoreValueIconDto | null {
  return ICON_TO[key] ?? null;
}

// ── Rows ────────────────────────────────────────────────────────────────────

/**
 * A location's address is null for non-physical rows. The app's address control
 * takes a non-null value, so a blank address stands in — `physical` is what tells
 * the two cases apart, not an empty street.
 */
export function locationFromDto(l: LocationDto): CompanyLocation {
  return {
    locationGuid: l.locationGuid,
    name: l.name ?? '',
    // Field by field, not a spread: the server sends both `address: null` AND an
    // address object whose parts are individually null. A spread carries those
    // nulls into a model that promises strings, and they surface as a crash in
    // whatever renders them rather than as a mapping problem here.
    address: {
      street: l.address?.street ?? '',
      city: l.address?.city ?? '',
      state: l.address?.state ?? '',
      postalCode: l.address?.postalCode ?? '',
    },
    physical: l.physical,
    timeZone: l.timeZone ?? null,
    startDt: l.startDt ?? '',
    notes: l.notes ?? '',
    // Absent means active: the field is new, and a row that predates it is in use.
    active: l.active ?? true,
  };
}

export function payClassFromDto(p: PayClassDto): PayClass {
  return { payClassGuid: p.payClassGuid, name: p.label ?? '', overtimeEligible: !!p.overtimeEligible };
}

/**
 * The whole page. Note what is NOT recomputed here: phase status and
 * `currentPhase` come from the server, which derives them from whichever phase is
 * CURRENT. The app used to work them out from the phase number; doing both would
 * mean two answers to the same question.
 */
export function companyInfoFromDto(dto: CompanyDto): CompanyInfo {
  // Every string is defaulted. The DTO types say these are strings because the
  // contract says so, but a nullable column reaches the wire as null, and the
  // first one to do it crashed the locations list. The boundary is where that
  // gets absorbed — past here the app's own types are honest.
  const p = dto.profile ?? ({} as CompanyDto['profile']);
  return {
    legalName: p.legalName ?? '',
    shortName: p.shortName ?? '',
    tagline: p.tagline ?? '',
    mission: p.mission ?? '',
    vision: p.vision ?? '',
    founded: p.foundedDt ?? '',
    hq: p.headquarters ?? '',
    videoUrl: p.welcomeVideoUrl ?? '',
    currentPhase: dto.currentGrowthPhaseSortIndex ?? 0,
    phases: (dto.companyGrowthPhases ?? []).map(
      (ph): CompanyPhase => ({
        phaseGuid: ph.phaseGuid,
        num: ph.sortIndex ?? 0,
        label: ph.label ?? '',
        status: phaseStatusFromDto(ph.growthPhaseStatus),
        // Phrases, not numbers. Passed through verbatim.
        targetRevenue: ph.targetRevenue ?? '',
        years: ph.yearRange ?? '',
        description: ph.description ?? '',
      }),
    ),
    diamondStandard: (dto.diamondStandardPillars ?? []).map(
      (pl): DiamondPillar => ({
        pillarGuid: pl.pillarGuid,
        // The app's numeric `id` is display-order only; the guid is the identity.
        id: pl.sortIndex ?? 0,
        name: pl.pillarName ?? '',
        desc: pl.description ?? '',
      }),
    ),
    coreValues: (dto.coreValues ?? []).map(
      (v, i): CompanyCoreValue => ({
        coreValueGuid: v.coreValueGuid,
        // Core values carry no sortIndex on the wire — they arrive in order, so
        // position IS the order and the index stands in for the legacy id.
        id: i,
        title: v.title ?? '',
        icon: iconFromDto(v.icon),
        body: v.body ?? '',
      }),
    ),
  };
}

export function locationsFromDto(dto: CompanyDto): CompanyLocation[] {
  return (dto.locations ?? []).map(locationFromDto);
}

export function payClassesFromDto(dto: CompanyDto): PayClass[] {
  return (dto.payClasses ?? []).map(payClassFromDto);
}
